import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Button,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useCompetencia } from '../../../../context/CompetenciaContext';
import AddCategoriaForm from './AddCategoriaForm';
import PropTypes from 'prop-types';
import predefinedCategorias from '../../../../constants/predefinedCategorias';
import CategoriasTable from './CategoriasTable';
import PredefinedCategoriasModal from './PredefinedCategoriasModal';
import SigertreLoader from '../../../Common/SigertreLoader';
import MobileCategoriasView from './Movil/MobileCategoriasView';
import { useTheme, useMediaQuery } from '@mui/material';
import CategoryDetailModal from './Movil/CategoryDetailModal';

// ✅ nuevos diálogos reutilizables
import {
  AddCategoriaDialog,
  ConfirmDeleteDialog,
  FilenameDialog,
  ImportErrorsDialog,
} from './Dialogs';

const CategoriasManager = ({ competencia }) => {
  const {
    addCategoria,
    addCategorias,
    updateCategoria,
    reorderAndUpdateCategorias,
    deleteCategoria,
    deleteCategorias,
    exportCategoriasToCSV,
    importCategoriasFromCSV,
    getCategorias,
    getDefaultFechaCorte,
  } = useCompetencia();

  // Estados
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errorModalContent, setErrorModalContent] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openFilenameDialog, setOpenFilenameDialog] = useState(false);
  const [filename, setFilename] = useState('');
  const [openAddCategoriaModal, setOpenAddCategoriaModal] = useState(false);
  const [openPredefinedCategoriasModal, setOpenPredefinedCategoriasModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedCategoriasIds, setSelectedCategoriasIds] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [isLoading, setIsLoading] = useState({
    categorias: true,
    acciones: false,
  });
  const [mobileSearch, setMobileSearch] = useState('');
  const [detailId, setDetailId] = React.useState(null);

  const openDetail = (id) => setDetailId(id);
  const closeDetail = () => setDetailId(null);

  const categoriaSeleccionada = React.useMemo(
    () => categorias.find((c) => c.id === detailId),
    [categorias, detailId]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Orden utilitario
  const sortCategorias = useCallback((cats) => {
    if (!Array.isArray(cats)) return [];
    return [...cats].sort((a, b) => (a.catNumber || 0) - (b.catNumber || 0));
  }, []);

  // Cargar y ordenar
  const loadAndSortCategorias = useCallback(async () => {
    if (!competencia.id) {
      setCategorias([]);
      setIsLoading({ categorias: false, acciones: false });
      return;
    }
    setIsLoading((prev) => ({ ...prev, categorias: true }));
    try {
      const cats = await getCategorias(competencia.id);
      setCategorias(sortCategorias(cats));
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al cargar categorías', severity: 'error' });
      setCategorias([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, categorias: false }));
    }
  }, [competencia.id, getCategorias, sortCategorias]);

  useEffect(() => {
    loadAndSortCategorias();
  }, [loadAndSortCategorias]);

  if (!competencia || !competencia.id) {
    return <Typography>Competencia no disponible</Typography>;
  }

  if (isLoading.categorias) {
    return (
      <SigertreLoader loaderText="SIGERTRE" message="Cargando categorías..." fullScreen />
    );
  }

  // Eliminar confirmada
  const handleConfirmDelete = async () => {
    if (selectedCategoriasIds.length === 0) return;
    try {
      setIsLoading({ ...isLoading, acciones: true });
      let result;
      if (selectedCategoriasIds.length > 1) {
        result = await deleteCategorias(competencia.id, selectedCategoriasIds);
      } else {
        result = await deleteCategoria(competencia.id, selectedCategoriasIds[0]);
      }

      if (result.wasDeleted) {
        const currentCats = await getCategorias(competencia.id);
        const sortedCats = sortCategorias(currentCats);
        const renumberedCats = sortedCats.map((cat, index) => ({
          ...cat,
          catNumber: index + 1,
        }));
        await reorderAndUpdateCategorias(competencia.id, renumberedCats);
        await loadAndSortCategorias();

        setCategorias((prev) => prev.filter((cat) => !selectedCategoriasIds.includes(cat.id)));
        setSnackbar({
          open: true,
          message:
            selectedCategoriasIds.length > 1
              ? `${result.count || selectedCategoriasIds.length} categorías eliminadas exitosamente.`
              : 'Categoría eliminada exitosamente.',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: result.reason || `Error al eliminar ${selectedCategoriasIds.length > 1 ? 'categorías' : 'categoría'}.`,
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error inesperado: ${error.message}`, severity: 'error' });
    } finally {
      setSelectedCategoriasIds([]);
      setOpenDeleteDialog(false);
      setIsLoading({ ...isLoading, acciones: false });
    }
  };

  const handleReorderCategorias = async (_competenciaId, categoriasConNuevosNumeros) => {
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));
      const sortedForUpdate = sortCategorias(categoriasConNuevosNumeros);
      const result = await reorderAndUpdateCategorias(competencia.id, sortedForUpdate);
      if (result.success) {
        setCategorias(sortedForUpdate);
        setSnackbar({ open: true, message: 'Orden de categorías actualizado.', severity: 'success' });
      } else {
        throw new Error(result.reason || 'Error al guardar el nuevo orden.');
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error al reordenar: ${error.message}`, severity: 'error' });
      await loadAndSortCategorias();
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
    }
  };

  const requestDeleteCategorias = (ids) => {
    if (!ids || ids.length === 0) return;
    const existingIds = ids.filter((id) => categorias.some((cat) => cat.id === id));
    if (existingIds.length === 0) {
      setSnackbar({ open: true, message: 'Las categorías seleccionadas ya no existen.', severity: 'warning' });
      return;
    }
    setSelectedCategoriasIds(existingIds);
    setOpenDeleteDialog(true);
  };

  // Import CSV
  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));
      const result = await importCategoriasFromCSV(competencia.id, file);

      if (result.wasImported || result.errores?.length > 0) {
        await loadAndSortCategorias();
      }

      if (result.wasImported && result.count > 0) {
        setSnackbar({ open: true, message: `Categorías importadas: ${result.count}`, severity: 'success' });
      }

      if (result.errores?.length > 0) {
        const formattedErrors = result.errores.map((err) => ({
          fila: err.fila || 'N/A',
          catNumber: err.categoria?.['#Cat'] || 'N/A',
          codigo: err.categoria?.codigo || 'N/A',
          nombre: err.categoria?.nombre || 'N/A',
          edadMin: err.categoria?.edadMin || 'N/A',
          edadMax: err.categoria?.edadMax || 'N/A',
          fechaCorte: err.categoria?.fechaCorte || 'N/A',
          reasons: Array.isArray(err.errores) ? err.errores : [err.message || err.reason || 'Error desconocido'],
        }));
        setErrorModalContent(formattedErrors);
        setOpenErrorModal(true);
        setSnackbar({ open: true, message: 'Algunas categorías no se importaron debido a errores.', severity: 'warning' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Error al importar', severity: 'error' });
      await loadAndSortCategorias();
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
      e.target.value = null;
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (categorias.length === 0) {
      setSnackbar({ open: true, message: 'No hay categorías para descargar.', severity: 'warning' });
      return;
    }
    setOpenFilenameDialog(true);
  };

  const performDownload = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));
      await exportCategoriasToCSV(competencia.id, filename);
      setSnackbar({ open: true, message: 'Categorías descargadas exitosamente.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al descargar categorías', severity: 'error' });
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
      setOpenFilenameDialog(false);
    }
  };

  // Añadir categoría
  const handleAddCategoria = async (categoria) => {
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));
      const sortedCategorias = [...categorias].sort((a, b) => (a.catNumber || 0) - (b.catNumber || 0));

      const nombreTrim = (categoria.nombre || '').trim().toLowerCase();
      const codigoTrim = (categoria.codigo || '').trim().toUpperCase();

      const nombreExistente = sortedCategorias.some(
        (cat) => (cat.nombre || '').trim().toLowerCase() === nombreTrim
      );
      if (nombreExistente) throw new Error('Ya existe una categoría con este nombre');

      const codigoExistente = sortedCategorias.some(
        (cat) => (cat.codigo || '').trim().toUpperCase() === codigoTrim
      );
      if (codigoExistente) throw new Error('Ya existe una categoría con este código');

      if (!nombreTrim || !codigoTrim) throw new Error('Nombre y código son campos obligatorios');

      const edadMin = Number(categoria.edadMin);
      const edadMax = Number(categoria.edadMax);
      if (isNaN(edadMin)) throw new Error('Edad mínima debe ser un número');
      if (isNaN(edadMax)) throw new Error('Edad máxima debe ser un número');
      if (edadMin > edadMax) throw new Error('Edad mínima no puede ser mayor que edad máxima');

      const nextCatNumber = (sortedCategorias[sortedCategorias.length - 1]?.catNumber || 0) + 1;

      const categoriaData = {
        nombre: nombreTrim.replace(/\s+/g, ' ').trim(),
        codigo: codigoTrim,
        edadMin,
        edadMax,
        fechaCorte: categoria.fechaCorte || competencia.general?.fechaFinCompetencia || getDefaultFechaCorte(),
        catNumber: categoria.catNumber ?? nextCatNumber,
      };

      const result = await addCategoria(competencia.id, categoriaData);
      if (!result?.wasAdded) {
        const error = new Error(result?.reason || 'Error desconocido');
        error.field = result?.field || 'general';
        throw error;
      }

      await loadAndSortCategorias();
      setSnackbar({ open: true, message: 'Categoría añadida exitosamente', severity: 'success' });
      setOpenAddCategoriaModal(false);
      return result;
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Error al añadir categoría', severity: 'error' });
      throw error;
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
    }
  };

  // Actualizar
  const handleUpdateCategoria = async (competenciaId, categoriaId, updatedData) => {
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));
      const { wasUpdated, reason } = await updateCategoria(competenciaId, categoriaId, updatedData);

      const nombreExistente = categorias.some(
        (cat) => cat.id !== categoriaId && cat.nombre.toLowerCase() === updatedData.nombre.trim().toLowerCase()
      );
      if (nombreExistente) throw new Error('Ya existe otra categoría con este nombre');

      if (wasUpdated) {
        setCategorias((prev) =>
          sortCategorias(prev.map((cat) => (cat.id === categoriaId ? { ...cat, ...updatedData } : cat)))
        );
        setSnackbar({ open: true, message: 'Categoría actualizada exitosamente.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: `No se pudo actualizar: ${reason}`, severity: 'warning' });
        await loadAndSortCategorias();
      }
      return { wasUpdated, reason };
    } catch (error) {
      setSnackbar({ open: true, message: `Error al actualizar: ${error.message}`, severity: 'error' });
      await loadAndSortCategorias();
      return { wasUpdated: false, reason: error.message };
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
    }
  };

  // Añadir predefinidas
  const handleAddPredefinedCategorias = async (categoriasToAdd) => {
    try {
      setIsLoading((prev) => ({ ...prev, acciones: true }));

      const categoriasUnicas = categoriasToAdd.filter(
        (cat, index, self) => index === self.findIndex((c) => c.codigo === cat.codigo && c.nombre === cat.nombre)
      );

      const result = await addCategorias(competencia.id, categoriasUnicas);

      const categoriasParaAgregar = categoriasUnicas.filter(
        (nuevaCat) =>
          !categorias.some((existente) => existente.nombre.toLowerCase() === nuevaCat.nombre.toLowerCase())
      );
      if (categoriasParaAgregar.length === 0) {
        throw new Error('Todas las categorías seleccionadas ya existen o son duplicadas');
      }

      if (result.wasAdded || result.errors?.length > 0) {
        await loadAndSortCategorias();
      }

      if (result.wasAdded && result.count > 0) {
        setSnackbar({ open: true, message: `Categorías predefinidas añadidas: ${result.count}`, severity: 'success' });
        setOpenPredefinedCategoriasModal(false);
      }

      if (result.errors?.length > 0) {
        const formattedErrors = result.errors.map((err) => ({
          fila: err.fila || 'N/A',
          catNumber: err.categoria?.['#Cat'] || 'N/A',
          codigo: err.categoria?.codigo || 'N/A',
          nombre: err.categoria?.nombre || 'N/A',
          edadMin: err.categoria?.edadMin || 'N/A',
          edadMax: err.categoria?.edadMax || 'N/A',
          fechaCorte: err.categoria?.fechaCorte || 'N/A',
          reasons: Array.isArray(err.errores) ? err.errores : [err.message || err.reason || 'Error desconocido'],
        }));
        setErrorModalContent(formattedErrors);
        setOpenErrorModal(true);
        setSnackbar({ open: true, message: 'Algunas categorías predefinidas no se añadieron.', severity: 'warning' });
      } else {
        if (result.wasAdded) setOpenPredefinedCategoriasModal(false);
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' });
      await loadAndSortCategorias();
    } finally {
      setIsLoading((prev) => ({ ...prev, acciones: false }));
    }
  };

  // Dialog filename
  const renderFilenameDialog = () => (
    <FilenameDialog
      open={openFilenameDialog}
      onClose={() => setOpenFilenameDialog(false)}
      onConfirm={performDownload}
      confirming={isLoading.acciones}
    >
      <TextField
        autoFocus
        margin="dense"
        label="Nombre del Archivo"
        fullWidth
        variant="outlined"
        value={filename}
        onChange={(e) => setFilename(e.target.value)}
      />
    </FilenameDialog>
  );

  return (
    <Box mb={4}>
      <Typography variant="h6" gutterBottom>
        Gestión de Categorías
      </Typography>

      <Grid container spacing={2} disableEqualOverflow justifyContent="flex-start" alignItems="stretch">
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            onClick={() => setOpenAddCategoriaModal(true)}
            startIcon={<AddIcon />}
            disabled={isLoading.acciones}
            sx={(t) => ({
              borderRadius: 999,
              px: { xs: 1.25, sm: 1.75 },
              py: 1.1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              bgcolor: 'transparent',
              color: t.palette.primary.main,
              border: `1px solid ${alpha(t.palette.primary.main, 0.35)}`,
              '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.06) },
            })}
          >
            {isLoading.acciones ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Personalizada
          </Button>
        </Grid>

<Grid item xs={12} sm={6} md={3}>
  <Button
    fullWidth
    onClick={() => setOpenPredefinedCategoriasModal(true)}
    startIcon={<AddIcon />}
    disabled={isLoading.acciones}
      sx={(t) => ({
        borderRadius: 999,
        px: { xs: 1.25, sm: 1.75 },
        py: 1.1,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: { xs: '0.85rem', sm: '0.9rem' },
        bgcolor: alpha(t.palette.secondary.main, 0.08),
        color: t.palette.secondary.main,
        border: `1px solid ${alpha(t.palette.secondary.main, 0.25)}`,
        boxShadow: `0 4px 18px ${alpha(t.palette.secondary.main, 0.18)}`,
        '&:hover': {
          bgcolor: alpha(t.palette.secondary.main, 0.12),
          boxShadow: `0 8px 28px ${alpha(t.palette.secondary.main, 0.25)}`,
        },
      })}
  >
    {isLoading.acciones ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
    Predeterminadas
  </Button>
</Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={isLoading.acciones}
            sx={(t) => ({
              borderRadius: 999,
              px: { xs: 1.25, sm: 1.75 },
              py: 1.1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              bgcolor: 'transparent',
              color: t.palette.primary.main,
              border: `1px solid ${alpha(t.palette.primary.main, 0.35)}`,
              '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.06) },
              '&.Mui-disabled': {
                color: alpha(t.palette.text.primary, 0.35),
                borderColor: alpha(t.palette.text.primary, 0.12),
              },
            })}
          >
            {isLoading.acciones ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Importar CSV
            <input type="file" accept=".csv" hidden onChange={handleImportCSV} />
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            onClick={handleExportCSV}
            startIcon={isLoading.acciones ? <CircularProgress size={18} /> : <DownloadIcon />}
            disabled={isLoading.acciones}
            sx={(t) => ({
              borderRadius: 999,
              px: { xs: 1.25, sm: 1.75 },
              py: 1.1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              bgcolor: 'transparent',
              color: t.palette.secondary.main,
              border: `1px solid ${alpha(t.palette.secondary.main, 0.35)}`,
              '&:hover': { bgcolor: alpha(t.palette.secondary.main, 0.06) },
              '&.Mui-disabled': {
                color: alpha(t.palette.text.primary, 0.35),
                borderColor: alpha(t.palette.text.primary, 0.12),
              },
            })}
          >
            {isLoading.acciones ? 'Preparando…' : 'Descargar CSV'}
          </Button>
        </Grid>
      </Grid>

      <Box mt={2.5}>
        {isMobile ? (
          <MobileCategoriasView
            categorias={categorias}
            search={mobileSearch}
            setSearch={setMobileSearch}
            fechaCorteDefault={competencia.general?.fechaFinCompetencia || getDefaultFechaCorte()}
            onOpenAddPersonalizada={() => setOpenAddCategoriaModal(true)}
            onOpenAddPredefinidas={() => setOpenPredefinedCategoriasModal(true)}
            onOpenDetalle={openDetail}
            competenciaId={competencia.id}
            onReorderCategorias={handleReorderCategorias}
            onRequestDeleteMultiple={(ids) => requestDeleteCategorias(ids)}
          />
        ) : (
          <CategoriasTable
            competenciaId={competencia.id}
            categorias={categorias}
            setSearch={setMobileSearch}
            onUpdateCategoria={handleUpdateCategoria}
            onRequestDeleteMultiple={requestDeleteCategorias}
            onRequestDeleteSingle={(id) => requestDeleteCategorias([id])}
            onDeleteCategoria={(id) => {
              setSelectedCategoriasIds([id]);
              setOpenDeleteDialog(true);
            }}
            onReorderCategorias={handleReorderCategorias}
            fechaCorte={competencia.general?.fechaFinCompetencia || getDefaultFechaCorte()}
            setSnackbar={setSnackbar}
            selectedCategoriasIds={selectedCategoriasIds}
            setSelectedCategoriasIds={setSelectedCategoriasIds}
          />
        )}
      </Box>

      {!!detailId && (
        <CategoryDetailModal
          open={!!detailId}
          onClose={closeDetail}
          competenciaId={competencia.id}
          categoria={categoriaSeleccionada}
          onUpdateCategoria={handleUpdateCategoria}
          setSnackbar={setSnackbar}
        />
      )}

      {/* ✅ Diálogo: Añadir Categoría (refactor) */}
      <AddCategoriaDialog open={openAddCategoriaModal} onClose={() => setOpenAddCategoriaModal(false)}>
        {/* Asegúrate que AddCategoriaForm renderice un <form id="add-categoria-form" ...> */}
        <AddCategoriaForm
          handleAddCategoria={handleAddCategoria}
          setSnackbar={setSnackbar}
          categoriasExistentes={categorias || []}
        />
      </AddCategoriaDialog>

      {/* Modal categorías predefinidas (tu componente) */}
      <PredefinedCategoriasModal
        open={openPredefinedCategoriasModal}
        handleClose={() => setOpenPredefinedCategoriasModal(false)}
        handleAddSelectedCategorias={handleAddPredefinedCategorias}
        predefinedCategorias={predefinedCategorias}
      />

      {/* ✅ Diálogo: Confirmación de eliminación (refactor) */}
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        count={selectedCategoriasIds.length}
        loading={isLoading.acciones}
      />

      {/* ✅ Diálogo: nombre de archivo (refactor) */}
      {renderFilenameDialog()}

      {/* ✅ Diálogo: errores de importación (refactor) */}
      <ImportErrorsDialog
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        rows={errorModalContent}
      />

      {/* Snackbar global */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isLoading.acciones && (
        <Box
          position="fixed"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(0,0,0,0.3)"
          zIndex={1300}
        >
          <SigertreLoader loaderText="SIGERTRE" message="Cargando" fullScreen />
        </Box>
      )}
    </Box>
  );
};

CategoriasManager.propTypes = {
  competencia: PropTypes.shape({
    id: PropTypes.string.isRequired,
    nombre: PropTypes.string.isRequired,
    general: PropTypes.shape({
      fechaFinCompetencia: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default CategoriasManager;
